import { CommentFrameEntry, Frame } from 'dragonmouse-shared/interfaces';
import { useEffect, useMemo, useRef, useState } from 'react';
import Comment from './Comment';
import CommentInput from './CommentInput';
import * as S from './styles';

interface CommentsProps {
	frame: Frame;
	filterElementId?: string;
}

const Comments = ({ frame, filterElementId }: CommentsProps) => {
	const bottomRef = useRef<HTMLDivElement>(null);
	// Which thread the reply composer is open on, and whose comment is being
	// answered — that author gets the prefilled mention. Threads are flat:
	// replying to a reply still posts under the thread root.
	const [replyingTo, setReplyingTo] = useState<{
		threadId: string;
		replyToUserId: string;
	} | null>(null);

	//----------------------------------------------------------------------
	// HOOKS
	//----------------------------------------------------------------------

	// All non-deleted comment entries, sorted chronologically
	const allComments = useMemo(
		() =>
			(frame.entries || [])
				.filter((entry): entry is CommentFrameEntry => entry.kind === 'comment' && !entry.deletedAt)
				.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
		[frame.entries]
	);

	// Top-level comments (no parentId), optionally filtered by elementId
	const topLevelComments = useMemo(
		() =>
			allComments
				.filter((c) => !c.parentId)
				.filter((c) => (filterElementId ? c.elementId === filterElementId : true)),
		[allComments, filterElementId]
	);

	// Replies grouped by parent id
	const repliesByParentId = useMemo(() => {
		const map = new Map<string, CommentFrameEntry[]>();
		for (const c of allComments) {
			if (c.parentId) {
				const list = map.get(c.parentId) ?? [];
				list.push(c);
				map.set(c.parentId, list);
			}
		}
		return map;
	}, [allComments]);

	// scroll to bottom when a new comment is added or the panel is shown
	useEffect(() => {
		bottomRef.current?.scrollIntoView({ block: 'end' });
	}, [allComments.length]);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ block: 'end' });
	}, []);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onStartReply = (threadId: string, replyToUserId: string) =>
		setReplyingTo({ threadId, replyToUserId });
	const onCancelReply = () => setReplyingTo(null);
	const onSubmitReply = () => setReplyingTo(null);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderThread = (parent: CommentFrameEntry) => {
		const replies = repliesByParentId.get(parent.id) ?? [];
		const isReplyingHere = replyingTo !== null && replyingTo.threadId === parent.id;
		return (
			<S.Thread key={parent.id}>
				<Comment
					comment={parent}
					frame={frame}
					onStartReply={() => onStartReply(parent.id, parent.userId)}
				/>
				{(replies.length > 0 || isReplyingHere) && (
					<S.ReplyList>
						{replies.map((reply) => (
							<Comment
								key={reply.id}
								comment={reply}
								frame={frame}
								isReply
								onStartReply={() => onStartReply(parent.id, reply.userId)}
							/>
						))}
						{isReplyingHere && (
							<CommentInput
								// Remount when the reply target changes so the
								// prefilled mention follows the clicked row.
								key={replyingTo.replyToUserId}
								frame={frame}
								elementId={parent.elementId ?? undefined}
								parentId={parent.id}
								replyToUserId={replyingTo.replyToUserId}
								autoFocus
								onCancel={onCancelReply}
								onSubmit={onSubmitReply}
							/>
						)}
					</S.ReplyList>
				)}
			</S.Thread>
		);
	};

	return (
		<S.Base>
			<S.ScrollingArea>{topLevelComments.map(renderThread)}</S.ScrollingArea>
			<CommentInput frame={frame} elementId={filterElementId} />
			<div ref={bottomRef} />
		</S.Base>
	);
};

export default Comments;
